// JetSetter Custom Hooks

import { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from '../utils';

// Export specific hooks
export { default as useProfile } from './useProfile';
export { default as useBookingHistory } from './useBookingHistory';
export { default as usePassengers } from './usePassengers';
export { default as useTravelPreferences } from './useTravelPreferences';
export { default as useMessages } from './useMessages';
export { default as useNotifications } from './useNotifications';

/**
 * Custom hook for form state management
 * @param {object} initialState - Initial form state
 * @param {function} validationFn - Validation function
 * @returns {object} - Form state and handlers
 */
export const useForm = (initialState = {}, validationFn = null) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use ref to store initial state to avoid recreating reset function
  const initialStateRef = useRef(initialState);
  
  // Update ref when initial state changes
  useEffect(() => {
    initialStateRef.current = initialState;
  }, [initialState]);

  const handleChange = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleBlur = useCallback((name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate with full form values so cross-field rules (e.g. mobile + country) work
    if (validationFn) {
      const fieldErrors = validationFn(values);
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] || '' }));
    }
  }, [values, validationFn]);

  const validate = useCallback(() => {
    if (!validationFn) return true;
    
    const validationErrors = validationFn(values);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [values, validationFn]);

  const reset = useCallback((newValues = null) => {
    const valuesToSet = newValues || initialStateRef.current;
    setValues(valuesToSet);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, []); // Now stable since we use ref // Remove initialState dependency to make reset stable

  return {
    values,
    errors,
    touched,
    isSubmitting,
    setIsSubmitting,
    setErrors,
    handleChange,
    handleBlur,
    validate,
    reset
  };
};
/**
 * Custom hook for click outside detection
 * @param {function} callback - Function to call when clicked outside
 * @returns {object} - Ref to attach to element
 */
export const useClickOutside = (callback) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [callback]);

  return ref;
};

/**
 * Custom hook for debounced value
 * @param {any} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} - Debounced value
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for local storage
 * @param {string} key - Storage key
 * @param {any} initialValue - Initial value
 * @returns {array} - [value, setValue] tuple
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading sessionStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting sessionStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
};